import * as React from 'react';
import styled from '@emotion/styled';
import { ChartLegend, OverlappedBarsChart, RadioButton } from '@liveoptics/react-ui';
import { Series } from '@liveoptics/react-ui/dist/types/interfaces/Charts';
import { OverProvisioningPoint } from '../models/views/overProvisioningGraphViewModel';
import { OverProvisioningGraphOption } from '../models/views/overProvisioningGraphViewModel';

const ChartContainer = styled.div`
  position: absolute;
  display: contents;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-top: 20px;
`;

const TooltipP = styled.p`
  font-family: 'Open Sans', sans-serif;
  color: #566072;
  font-weight: 500;
`;

const RadioButtonLabel = styled.label`
  padding-right: 30px;
`;

const LegendWrapper = styled.div`
  align-self: flex-end;
  margin-bottom: 25px;
  min-width: max-content;
`;

const RadioButtonsContainer = styled.div`
  padding: 37px 0px 0px 380px;
`;

const DescriptionParagraph = styled.p`
  max-width: 900px;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.28px;
  color: #566072;
  opacity: 1;
  user-select: none;
  flex: 1;
  text-align: justify;
  margin-bottom: 0px;
  margin-right: 4rem;
`;

interface Props {
  data: OverProvisioningPoint[] | undefined;
  showGraph: boolean;
}

interface State {
  selectedGraph: OverProvisioningGraphOption;
}

const CheckIfHasValue = (toCheck: any) => {
  return toCheck || toCheck === 0;
};

const updateGraphValues = (
  vms: OverProvisioningPoint[],
  graphType: OverProvisioningGraphOption
): Series<OverProvisioningPoint>[][] => {
  let chartSeries: Series<OverProvisioningPoint>[][] = [];
  let provisionedWithUsedSubSeries: Series<OverProvisioningPoint>[] = [];
  let provisionedWithoutUsedSubSeries: Series<OverProvisioningPoint>[] = [];
  let provisionedSeries: Series<OverProvisioningPoint> = {
    points: [],
    key: 'provisionedSeries'
  };
  let UsedSeries: Series<OverProvisioningPoint> = { points: [], key: 'UsedSeries' };
  let provisionedWithoutUsedSeries: Series<OverProvisioningPoint> = {
    points: [],
    key: 'provisionedWithoutUsedSeries'
  };

  switch (graphType) {
    case OverProvisioningGraphOption.CapacityInGB:
      vms.slice().sort(
        (a, b) =>
          ((b.UsedCapacityGB !== undefined && b.UsedCapacityGB !== null) ? b.ProvisionedCapacityGB - b.UsedCapacityGB : 0) -
            ((a.UsedCapacityGB !== undefined && a.UsedCapacityGB !== null) ? a.ProvisionedCapacityGB - a.UsedCapacityGB : 0) ||
          b.ProvisionedCapacityGB - a.ProvisionedCapacityGB
      ).map(vm => {
        let provisionedPoint = { ...vm };
        provisionedPoint.value = vm.ProvisionedCapacityGB;

        if (vm.UsedCapacityGB || vm.UsedCapacityGB === 0) {
          provisionedSeries.points.push(provisionedPoint);

          let UsedPoint = { ...vm };
          UsedPoint.value = vm.UsedCapacityGB;
          UsedSeries.points.push(UsedPoint);
        } else {
          provisionedWithoutUsedSeries.points.push(provisionedPoint);
        }
      });
      break;
    case OverProvisioningGraphOption.PeakMemory:
      vms.slice().sort(
        (a, b) =>
          ((b.PeakMemoryGB !== undefined && b.PeakMemoryGB !==null)  ? b.ProvisionedMemoryGB - b.PeakMemoryGB : 0) -
            ((a.PeakMemoryGB !== undefined && a.PeakMemoryGB !==null) ? a.ProvisionedMemoryGB - a.PeakMemoryGB : 0) ||
          b.ProvisionedMemoryGB - a.ProvisionedMemoryGB
      ).map(vm => {
        let provisionedPoint = { ...vm };
        provisionedPoint.value = vm.ProvisionedMemoryGB;

        if (vm.PeakMemoryGB || vm.PeakMemoryGB === 0) {
          provisionedSeries.points.push(provisionedPoint);

          let UsedPoint = { ...vm };
          UsedPoint.value = vm.PeakMemoryGB;
          UsedSeries.points.push(UsedPoint);
        } else {
          provisionedWithoutUsedSeries.points.push(provisionedPoint);
        }
      });
      break;
      case OverProvisioningGraphOption.AvgMemory:
        vms.slice().sort(
          (a, b) =>
            ((b.AvgMemoryGB !== undefined && b.AvgMemoryGB !==null)  ? b.ProvisionedMemoryGB - b.AvgMemoryGB : 0) -
              ((a.AvgMemoryGB !== undefined && a.AvgMemoryGB !==null) ? a.ProvisionedMemoryGB - a.AvgMemoryGB : 0) ||
            b.ProvisionedMemoryGB - a.ProvisionedMemoryGB
        ).map(vm => {
          let provisionedPoint = { ...vm };
          provisionedPoint.value = vm.ProvisionedMemoryGB;
  
          if (vm.AvgMemoryGB || vm.AvgMemoryGB === 0) {
            provisionedSeries.points.push(provisionedPoint);
  
            let UsedPoint = { ...vm };
            UsedPoint.value = vm.AvgMemoryGB;
            UsedSeries.points.push(UsedPoint);
          } else {
            provisionedWithoutUsedSeries.points.push(provisionedPoint);
          }
        });
        break;
    case OverProvisioningGraphOption.AverageVcpu:
        vms.slice().sort(
          (a, b) =>
          ((b.AvgVCpu !== undefined && b.AvgVCpu !== null) ? b.ProvisionedVCpu - b.AvgVCpu : 0) -
            ((a.AvgVCpu !== undefined && a.AvgVCpu !== null) ? a.ProvisionedVCpu - a.AvgVCpu : 0) ||
          b.ProvisionedVCpu - a.ProvisionedVCpu 
            ).map(vm => { 
            let provisionedPoint = { ...vm };
            provisionedPoint.value = vm.ProvisionedVCpu;
            if (vm.AvgVCpu || vm.AvgVCpu === 0) {
              provisionedSeries.points.push(provisionedPoint);
    
              let UsedPoint = { ...vm };
              UsedPoint.value = vm.AvgVCpu;
              UsedSeries.points.push(UsedPoint);
            } else {
              provisionedWithoutUsedSeries.points.push(provisionedPoint);
          }
        });
      break;
    case OverProvisioningGraphOption.FreePeakVcpu:
    default:
        vms.slice().sort(
          (a, b) =>
          ((b.PeakVCpu !== undefined && b.PeakVCpu !== null) ? b.ProvisionedVCpu - b.PeakVCpu : 0) -
            ((a.PeakVCpu !== undefined && a.PeakVCpu !== null) ? a.ProvisionedVCpu - a.PeakVCpu : 0) ||
          b.ProvisionedVCpu - a.ProvisionedVCpu 
           ).map(vm => { 
            let provisionedPoint = { ...vm };
            provisionedPoint.value = vm.ProvisionedVCpu;
            if (vm.PeakVCpu || vm.PeakVCpu === 0) {
              provisionedSeries.points.push(provisionedPoint);
    
              let UsedPoint = { ...vm };
              UsedPoint.value = vm.PeakVCpu;
              UsedSeries.points.push(UsedPoint);
            } else {
              provisionedWithoutUsedSeries.points.push(provisionedPoint);
          }
        });
        break;
  }

  provisionedSeries.color = '#4694CE';
  UsedSeries.color = '#F2AF00';
  provisionedWithoutUsedSeries.color = '#AAAAAA';

  provisionedWithUsedSubSeries.push(provisionedSeries);
  provisionedWithUsedSubSeries.push(UsedSeries);
  provisionedWithoutUsedSubSeries.push(provisionedWithoutUsedSeries);

  chartSeries.push(provisionedWithUsedSubSeries);
  chartSeries.push(provisionedWithoutUsedSubSeries);

  return chartSeries;
};

class OverProvisioningPerfGraph extends React.Component<Props> {
  state = {
    selectedGraph: OverProvisioningGraphOption.FreePeakVcpu
  } as State;

  render() {
    const OverProvisioningChart = (currentSeries: Series<OverProvisioningPoint>[][]) => {
      return (
        <OverlappedBarsChart
          height={300}
          matrix={currentSeries}
          tooltipFn={point => GetTooltipText(point as OverProvisioningPoint)}
        ></OverlappedBarsChart>
      );
    };

    const GetTooltipText = (point: OverProvisioningPoint) => {
      switch (this.state.selectedGraph) {        
        case OverProvisioningGraphOption.FreePeakVcpu:
          return (
            <TooltipP>
              VM Name: {point.Name} <br />
              Provisioned vCPU: {point.ProvisionedVCpu} <br />
              {CheckIfHasValue(point.PeakVCpu) ? `Peak Used vCPU: ${point.PeakVCpu}` : ''}
            </TooltipP>
          );
        case OverProvisioningGraphOption.AverageVcpu:
        return (
          <TooltipP>
            VM Name: {point.Name} <br />
            Provisioned vCPU: {point.ProvisionedVCpu} <br />
            {CheckIfHasValue(point.PeakVCpu) ? `Avg. Used vCPU: ${point.AvgVCpu}` : ''}
          </TooltipP>
        );
        case OverProvisioningGraphOption.PeakMemory:
        return (
          <TooltipP>
            VM Name: {point.Name} <br />
            Provisioned Memory: {point.ProvisionedMemoryGB} <br />
            {CheckIfHasValue(point.PeakMemoryGB) ? `Peak Used (Active) Memory: ${point.PeakMemoryGB}` : ''}
          </TooltipP>
        );
        case OverProvisioningGraphOption.AvgMemory:
        return (
          <TooltipP>
            VM Name: {point.Name} <br />
            Provisioned Memory: {point.ProvisionedMemoryGB} <br />
            {CheckIfHasValue(point.AvgMemoryGB) ? `Avg. Used (Active) Memory: ${point.AvgMemoryGB}` : ''}
          </TooltipP>
        );
        case OverProvisioningGraphOption.CapacityInGB:
        default:
          return (
            <TooltipP>
              VM Name: {point.Name} <br />
              Provisioned Capacity: {point.ProvisionedCapacityGB} <br />
              {CheckIfHasValue(point.UsedCapacityGB)
                ? `Used Capacity: ${point.UsedCapacityGB}`
                : ''}
            </TooltipP>
          );
      }
    };

    const legendItems = [];
    var selectedGraph = this.state.selectedGraph;
    if (
      this.props.data &&
      this.props.data.some(
        e =>
          (selectedGraph === OverProvisioningGraphOption.CapacityInGB &&
            CheckIfHasValue(e.UsedCapacityGB)) ||
          (selectedGraph === OverProvisioningGraphOption.FreePeakVcpu &&
            CheckIfHasValue(e.PeakVCpu)) ||
          (selectedGraph === OverProvisioningGraphOption.AverageVcpu &&
            CheckIfHasValue(e.AvgVCpu)) ||
          (selectedGraph === OverProvisioningGraphOption.PeakMemory &&
            CheckIfHasValue(e.PeakMemoryGB)) ||
          (selectedGraph === OverProvisioningGraphOption.AvgMemory &&
            CheckIfHasValue(e.AvgMemoryGB)) 
      )
    ) 
    {
      switch(selectedGraph){
      case OverProvisioningGraphOption.FreePeakVcpu:
        legendItems.push(
          { color: '#4694CE', label: `Provisioned vCPU` },
          { color: '#F2AF00', label: `Peak Used vCPU` }
        );
        break;
      case OverProvisioningGraphOption.AverageVcpu:
        legendItems.push(
          { color: '#4694CE', label: `Provisioned vCPU` },
          { color: '#F2AF00', label: `Avg. Used vCPU` }
        );
        break;
      case OverProvisioningGraphOption.PeakMemory:
      legendItems.push(
        { color: '#4694CE', label: `Provisioned Memory` },
        { color: '#F2AF00', label: `Peak Used (Active) Memory` }
      );
        break;
      case OverProvisioningGraphOption.AvgMemory:
        legendItems.push(
          { color: '#4694CE', label: `Provisioned Memory` },
          { color: '#F2AF00', label: `Avg. Used (Active) Memory` }
        );
      break;
      case OverProvisioningGraphOption.CapacityInGB:
      default:
        legendItems.push(
          { color: '#4694CE', label: `Provisioned ${this.state.selectedGraph}` },
          { color: '#F2AF00', label: `Used ${this.state.selectedGraph}` }
        );
        break;
      }     
    }

    if (
      this.props.data &&
      this.props.data.some(
        e =>
          (selectedGraph === OverProvisioningGraphOption.CapacityInGB &&
            !CheckIfHasValue(e.UsedCapacityGB)) ||
          (selectedGraph === OverProvisioningGraphOption.FreePeakVcpu &&
            !CheckIfHasValue(e.PeakVCpu)) ||
          (selectedGraph === OverProvisioningGraphOption.AverageVcpu &&
            !CheckIfHasValue(e.AvgVCpu)) ||
          (selectedGraph === OverProvisioningGraphOption.PeakMemory &&
            !CheckIfHasValue(e.PeakMemoryGB)) ||
          (selectedGraph === OverProvisioningGraphOption.AvgMemory &&
            !CheckIfHasValue(e.AvgMemoryGB)) 
      )
    ) 
    {
      switch(selectedGraph){
      case OverProvisioningGraphOption.FreePeakVcpu:
        legendItems.push({
          color: '#AAAAAA',
          label: `Peak Used vCPU Unavailable`
        });
        break;
        case OverProvisioningGraphOption.AverageVcpu:
        legendItems.push({
          color: '#AAAAAA',
          label: `Avg. Used vCPU Unavailable`
        });
        break;
        case OverProvisioningGraphOption.PeakMemory:
          legendItems.push({
            color: '#AAAAAA',
            label: `Peak Used (Active) Memory Unavailable`
          });
          break;
        case OverProvisioningGraphOption.AvgMemory:
        legendItems.push({
          color: '#AAAAAA',
          label: `Avg. Used (Active) Memory Unavailable`
        });
        break;
        case OverProvisioningGraphOption.CapacityInGB:
        default:
          legendItems.push({
            color: '#AAAAAA',
            label: `Used ${this.state.selectedGraph} Unavailable`
          });
          break;
      }     
    }

    const radioButtons = (
      <div>
         <RadioButton
          id="radioFreePeakVCpu"
          type="radio"
          checked={this.state.selectedGraph === OverProvisioningGraphOption.FreePeakVcpu}
          onClick={() => {
            this.setState({
              selectedGraph: OverProvisioningGraphOption.FreePeakVcpu
            });
          }}
        />
        <RadioButtonLabel htmlFor="radioFreePeakVCpu">Free Peak vCPU</RadioButtonLabel>

        <RadioButton
          id="radioAvgVcpu"
          type="radio"
          checked={this.state.selectedGraph === OverProvisioningGraphOption.AverageVcpu}
          onClick={() => {
            this.setState({
              selectedGraph: OverProvisioningGraphOption.AverageVcpu
            });
          }}
        />
        <RadioButtonLabel htmlFor="radioAvgVcpu">Free Avg. vCPU</RadioButtonLabel>

        <RadioButton
          id="radioPeakMemory"
          type="radio"
          checked={this.state.selectedGraph === OverProvisioningGraphOption.PeakMemory}
          onClick={() => {
            this.setState({
              selectedGraph: OverProvisioningGraphOption.PeakMemory
            });
          }}
        />
        <RadioButtonLabel htmlFor="radioPeakMemory">Peak Memory (GiB)</RadioButtonLabel>

        <RadioButton
          id="radioAvgMemory"
          type="radio"
          checked={this.state.selectedGraph === OverProvisioningGraphOption.AvgMemory}
          onClick={() => {
            this.setState({
              selectedGraph: OverProvisioningGraphOption.AvgMemory
            });
          }}
        />
        <RadioButtonLabel htmlFor="radioAvgMemory">Avg. Memory (GiB)</RadioButtonLabel>

        <RadioButton
          id="radioCapacityInGB"
          type="radio"
          checked={this.state.selectedGraph === OverProvisioningGraphOption.CapacityInGB}
          onClick={() => {
            this.setState({
              selectedGraph: OverProvisioningGraphOption.CapacityInGB
            });
          }}
        />
        <RadioButtonLabel htmlFor="radioCapacityInGB">Capacity (GiB)</RadioButtonLabel>        
      </div>
    );
    if (this.props.showGraph && this.props.data) {
      const currentSeries = updateGraphValues(this.props.data, this.state.selectedGraph);

      return (
        <ChartContainer>
          {currentSeries.length !== 0 && (
            <ChartContainer>
              <RadioButtonsContainer>{radioButtons}</RadioButtonsContainer>
              <ChartHeader>
                <DescriptionParagraph>
                  Over-Provisioning plots both the provisioned and used resources per VM, sorted by
                  the free resources. To learn more about how to read this chart see our{' '}
                  <a href="https://www.dell.com/support/kbdoc/000233567">blog</a>.
                </DescriptionParagraph>
                <LegendWrapper>
                  <ChartLegend legendItems={legendItems} />
                </LegendWrapper>
              </ChartHeader>
              {OverProvisioningChart(currentSeries)}
            </ChartContainer>
          )}
        </ChartContainer>
      );
    } else return <div></div>;
  }
}

export default OverProvisioningPerfGraph;
